import React from 'react';
import {useTranslation} from 'react-i18next';
import {fetcher, safeCaptureException} from '../utils';
import {API, BiomatchResponse, CompareBiomatchResponse} from '@chekin/api';
import {IMAGE_SERVICE_ORIGIN, IV_PHOTO_SOURCE, OCR_CHECKING_STATUSES} from '@chekin/core';
import {ANALYTIC_EVENTS, Analytics} from '../analytics';

const MaxRetriesNumber = 20;
const RetryTimeoutMs = 2000;

enum FaceDetectionOrigin {
  document = 'DOCUMENT',
  selfie = 'selfie',
}
type Options = {
  retries?: number;
};
type checkOCRResponse =
  | {
      mrzDetected?: boolean;
      detectionFailed?: boolean;
    }
  | undefined;

export type IVCheckProps = {
  image: string;
  countryCode: string;
  source: IV_PHOTO_SOURCE;
  reservationId?: string;
};

export type SendSelfieAndDocumentToCompareOptions = {
  personPhotoId: string;
  documentPhotoId: string;
  reservationId?: string;
};

type UseBiomatch = {
  onError?: (error: Error) => void;
  onFinish?: (image: string, data: BiomatchResponse) => void;
  onBefore?: () => void;
  onSuccessComparing?: (data: CompareBiomatchResponse) => void;
  onDetectionFailed?: (errorMessage?: string) => void;
  onRetriesFinish?: (
    data: BiomatchResponse | CompareBiomatchResponse,
    retries: number,
  ) => void;
};

function useBiomatch({
  onError,
  onFinish,
  onBefore,
  onDetectionFailed,
  onSuccessComparing,
  onRetriesFinish,
}: UseBiomatch) {
  const {t} = useTranslation();

  const retryTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(() => {
    const retryTimeout = retryTimeoutRef.current;
    const timeout = timeoutRef.current;

    return () => {
      clearTimeout(retryTimeout);
      clearTimeout(timeout);
    };
  }, []);

  const handleSuccessBiomatch = React.useCallback(
    (image: string, data: BiomatchResponse) => {
      onFinish?.(image, data);
    },
    [onFinish],
  );

  const checkDetectionFaceStatus = React.useCallback(
    (
      callback: (
        id: string,
      ) => ReturnType<typeof API.identityVerification.checkDocumentFaceDetectionStatus>,
      origin: FaceDetectionOrigin,
    ) => {
      const handler = async (
        id: string,
        image: string,
        options?: Options,
      ): Promise<checkOCRResponse> => {
        try {
          const data = await fetcher(callback, id);
          const isInProgress =
            data?.status === OCR_CHECKING_STATUSES.processing ||
            data?.status === OCR_CHECKING_STATUSES.new;
          const isSuccess =
            data?.status === OCR_CHECKING_STATUSES.complete && data?.is_face_detected;
          const isDetectionFailed =
            data?.status === OCR_CHECKING_STATUSES.error ||
            (data?.status === OCR_CHECKING_STATUSES.complete && !data?.is_face_detected);

          if (isInProgress) {
            const retries = options?.retries || 0;
            if (retries >= MaxRetriesNumber) {
              onRetriesFinish?.(data, retries);
              return;
            }

            retryTimeoutRef.current = setTimeout(async () => {
              await handler(id, image, {retries: retries + 1});
              return;
            }, RetryTimeoutMs);

            return;
          }
          Analytics.track(ANALYTIC_EVENTS.ivGettingResultsFromBiomatch, {
            check_id: data.id,
            picture_file: data.picture_file,
            is_face_detected: data.is_face_detected,
            status: data.status,
            service: IMAGE_SERVICE_ORIGIN.biomatch,
            retries: options?.retries ?? 0,
          });
          if (isSuccess) {
            handleSuccessBiomatch(image, data);
            return;
          }
          if (isDetectionFailed) {
            const faceErrorMessageTransKey =
              origin === FaceDetectionOrigin.document
                ? 'your_face_document_could_not_be_detected'
                : 'your_face_could_not_be_detected';
            onDetectionFailed?.(t(faceErrorMessageTransKey));
          }
        } catch (error) {
          safeCaptureException(error, 'Face detection status check failed');
          onError?.(error as Error);
        }
      };

      return handler;
    },
    [handleSuccessBiomatch, onDetectionFailed, onError, onRetriesFinish, t],
  );

  const sendAndVerifyDocumentForFaceDetection = React.useCallback(
    async ({image, countryCode, reservationId, source}: IVCheckProps) => {
      try {
        onBefore?.();
        const data = await fetcher(
          API.identityVerification.sendDocumentForFaceDetection,
          {
            picture_file: image,
            for_country: countryCode,
            reservation_id: reservationId,
            source,
          },
        );
        if (!data?.id) return;

        Analytics.track(ANALYTIC_EVENTS.ivPhotoSent, {
          check_id: data.id,
          picture_file: data.picture_file,
          status: data.status,
          service: IMAGE_SERVICE_ORIGIN.biomatch,
          step: 'document',
        });

        const checkDocumentFaceStatus = checkDetectionFaceStatus(
          API.identityVerification.checkDocumentFaceDetectionStatus,
          FaceDetectionOrigin.document,
        );
        await checkDocumentFaceStatus(data?.id, image);
      } catch (error) {
        safeCaptureException(error, 'Biomatch operation failed');
        onError?.(error as Error);
      }
    },
    [onError, onBefore, checkDetectionFaceStatus],
  );

  const sendAndVerifySelfieForFaceDetection = React.useCallback(
    async ({image, countryCode, reservationId, source}: IVCheckProps) => {
      try {
        onBefore?.();

        const data = await fetcher(API.identityVerification.sendSelfieForFaceDetection, {
          picture_file: image,
          for_country: countryCode,
          reservation_id: reservationId,
          source,
        });
        if (!data?.id) return;

        Analytics.track(ANALYTIC_EVENTS.ivPhotoSent, {
          check_id: data.id,
          picture_file: data.picture_file,
          status: data.status,
          service: IMAGE_SERVICE_ORIGIN.biomatch,
          step: 'selfie',
        });

        const checkSelfieStatus = checkDetectionFaceStatus(
          API.identityVerification.checkSelfieFaceDetection,
          FaceDetectionOrigin.selfie,
        );
        await checkSelfieStatus(data?.id, image);
      } catch (error) {
        safeCaptureException(error, 'Biomatch operation failed');
        onError?.(error as Error);
      }
    },
    [checkDetectionFaceStatus, onBefore, onError],
  );

  const checkSelfieAndDocumentCompareStatus = React.useCallback(
    async (compareId: string, options?: Options) => {
      try {
        const data = await fetcher(
          API.identityVerification.checkSelfieAndDocumentFacesComparingStatus,
          compareId,
        );
        const isInProgress =
          data?.status === OCR_CHECKING_STATUSES.processing ||
          data?.status === OCR_CHECKING_STATUSES.new;
        const isSuccess =
          data?.status === OCR_CHECKING_STATUSES.complete && data?.is_match;
        const isDetectionFailed =
          data?.status === OCR_CHECKING_STATUSES.error ||
          (data?.status === OCR_CHECKING_STATUSES.complete && !data?.is_match);

        if (isInProgress) {
          const retries = options?.retries || 0;
          if (retries >= MaxRetriesNumber) {
            onRetriesFinish?.(data, retries);
            return;
          }

          retryTimeoutRef.current = setTimeout(async () => {
            await checkSelfieAndDocumentCompareStatus(compareId, {retries: retries + 1});
            return;
          }, RetryTimeoutMs);

          return;
        }
        Analytics.track(ANALYTIC_EVENTS.ivGettingResultsFromBiomatchMatching, {
          check_id: data.id,
          status: data.status,
          distance: data.distance ?? 0,
          is_match: data.is_match,
          identification_picture: data.identification_picture,
          person_picture: data.person_picture,
          service: IMAGE_SERVICE_ORIGIN.biomatch,
          step: 'comparing',
          retries: options?.retries ?? 0,
        });
        if (isSuccess) {
          onSuccessComparing?.(data);
        }
        if (isDetectionFailed) {
          onDetectionFailed?.(t('matching_error'));
        }
      } catch (error) {
        safeCaptureException(error, 'Biomatch operation failed');
        onError?.(error as Error);
      }
    },
    [onSuccessComparing, onDetectionFailed, onRetriesFinish, onError, t],
  );

  const sendSelfieAndDocumentToCompare = React.useCallback(
    async ({
      reservationId,
      personPhotoId,
      documentPhotoId,
    }: SendSelfieAndDocumentToCompareOptions) => {
      try {
        onBefore?.();
        const data = await fetcher(
          API.identityVerification.compareSelfieAndDocumentFaces,
          {
            person_picture: personPhotoId,
            identification_picture: documentPhotoId,
            reservation_id: reservationId,
          },
        );
        if (!data?.id) return;

        Analytics.track(ANALYTIC_EVENTS.ivPhotoSent, {
          check_id: data.id,
          distance: data.distance ?? 0,
          is_match: data.is_match,
          identification_picture: data.identification_picture,
          person_picture: data.person_picture,
          status: data.status,
          service: IMAGE_SERVICE_ORIGIN.biomatch,
          step: 'comparing',
        });

        await checkSelfieAndDocumentCompareStatus(data?.id);
      } catch (error) {
        safeCaptureException(error, 'Biomatch operation failed');
        onError?.(error as Error);
      }
    },
    [onBefore, checkSelfieAndDocumentCompareStatus, onError],
  );

  return {
    sendAndVerifyDocumentForFaceDetection,
    sendAndVerifySelfieForFaceDetection,
    sendSelfieAndDocumentToCompare,
  };
}

export {useBiomatch};
